import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { IoPlay, IoImages, IoHeart, IoChatbubbleOutline } from "react-icons/io5";
import Avatar from "../../components/Avatar/Avatar";
import { extractPostMedia, formatCount } from "./exploreUtils";
import EP1 from "../../assets/images/EP1.jpg";
import styles from "./ExplorePostCard.module.scss";

const ExplorePostCard = ({
	post,
	variant = "grid", // "slider" | "grid" | "compact"
	className = "",
	isDragging = false,
}) => {
	const navigate = useNavigate();
	const [imgError, setImgError] = useState(false);
	const [isHovered, setIsHovered] = useState(false);
	const videoRef = useRef(null);

	if (!post) return null;

	const { url, isVideo, hasMultiple } = extractPostMedia(post);
	const fallbackUrl = EP1;
	const displayUrl = imgError || !url ? fallbackUrl : url;

	const author = post.userId || {};
	const authorName = author.username || author.displayName || author.GetstatedName || "User";
	const authorAvatar = author.ProfilePic || author.personaThumbnail;

	const likeCount =
		post.likes?.length !== undefined
			? post.likes.length
			: post.LikeCount !== undefined
			? post.LikeCount
			: 0;

	const commentCount =
		post.comments?.length !== undefined
			? post.comments.length
			: post.Comments?.length !== undefined
			? post.Comments.length
			: 0;

	const handleMouseEnter = () => {
		setIsHovered(true);
		if (isVideo && videoRef.current) {
			videoRef.current.play().catch(() => {
				// Autoplay might be restricted
			});
		}
	};

	const handleMouseLeave = () => {
		setIsHovered(false);
		if (isVideo && videoRef.current) {
			videoRef.current.pause();
			videoRef.current.currentTime = 0;
		}
	};

	const handleClick = (e) => {
		if (isDragging) {
			e.preventDefault();
			return;
		}
		if (post._id) {
			navigate(`/Post/${post._id}`);
		}
	};

	return (
		<div
			className={`${styles.card} ${styles[variant]} ${className}`}
			onClick={handleClick}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			role="button"
			tabIndex={0}
		>
			<div className={styles.mediaContainer}>
				{isVideo && !imgError ? (
					<video
						ref={videoRef}
						src={displayUrl}
						className={styles.media}
						preload="metadata"
						muted
						playsInline
						loop
						onError={() => setImgError(true)}
					/>
				) : (
					<img
						src={displayUrl}
						alt={post.caption || "Explore post"}
						className={styles.media}
						loading="lazy"
						onError={() => setImgError(true)}
					/>
				)}

				{/* Media Badges */}
				<div className={styles.badgeContainer}>
					{isVideo && (
						<span className={styles.mediaBadge} title="Video">
							<IoPlay />
						</span>
					)}
					{hasMultiple && (
						<span className={styles.mediaBadge} title="Multiple media">
							<IoImages />
						</span>
					)}
				</div>

				{/* Hover Info Overlay */}
				<div className={`${styles.overlay} ${isHovered ? styles.visible : ""}`}>
					<div className={styles.authorInfo}>
						<div className={styles.avatarWrap}>
							<Avatar
								src={authorAvatar}
								name={authorName}
								style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }}
							/>
						</div>
						<span className={styles.authorName}>{authorName}</span>
					</div>

					<div className={styles.statsRow}>
						<span className={styles.statItem}>
							<IoHeart className={styles.statIcon} />
							<span>{formatCount(likeCount)}</span>
						</span>
						<span className={styles.statItem}>
							<IoChatbubbleOutline className={styles.statIcon} />
							<span>{formatCount(commentCount)}</span>
						</span>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ExplorePostCard;
